import { useMemo, useState } from 'react';

import {
  ABUNDANCE_CHALLENGES,
  ABUNDANCE_QUESTION,
  abundanceFields,
  calculateAbundanceExplore,
  fillRemainder,
  reviewedPercents,
} from './atomicAbundanceExplore';
import { type AbundanceElement } from './atomicModels';
import { ExploreChallenges } from './ExploreChallenges';
import { ModelInputPanel, WorkedStepsPanel } from './ModelInputs';
import { useLastValidResult } from './useLastValidResult';

export function AbundanceChart({ element }: { element: AbundanceElement }) {
  const width = 320;
  const height = 170;
  const barWidth = 34;
  const masses = element.isotopes.map((isotope) => isotope.massNumber);
  const low = Math.min(...masses) - 1;
  const high = Math.max(...masses) + 1;
  const x = (mass: number) => 30 + ((mass - low) / (high - low)) * (width - 60);
  const y = (fraction: number) => height - 30 - fraction * (height - 60);
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="w-full"
      role="img"
      aria-label={`${element.name} isotopic abundances: ${element.isotopes
        .map((isotope) => `${element.symbol}-${isotope.massNumber} ${(isotope.abundance * 100).toFixed(2)}%`)
        .join(', ')}`}
    >
      <path d={`M20 ${height - 30}H${width - 10}`} stroke="currentColor" className="text-slate2" />
      {element.isotopes.map((isotope) => (
        <g key={isotope.massNumber}>
          <rect
            x={x(isotope.massNumber) - barWidth / 2}
            y={y(isotope.abundance)}
            width={barWidth}
            height={height - 30 - y(isotope.abundance)}
            rx="4"
            className="fill-brand-sky"
          />
          <text x={x(isotope.massNumber)} y={y(isotope.abundance) - 5} textAnchor="middle" className="fill-ink text-[10px] font-extrabold">
            {(isotope.abundance * 100).toFixed(2)}%
          </text>
          <text x={x(isotope.massNumber)} y={height - 14} textAnchor="middle" className="fill-slate2 text-[10px] font-bold">
            {element.symbol}-{isotope.massNumber}
          </text>
        </g>
      ))}
      <text x="20" y={height - 2} className="fill-slate2 text-[9px] font-bold">
        Mass number (bar height = abundance)
      </text>
    </svg>
  );
}

const calculateForInput = ({ element, percents }: { element: AbundanceElement; percents: Record<string, number> }) =>
  calculateAbundanceExplore(element, percents);

/**
 * Explore: the learner edits each isotope's abundance; Ar = Σ(isotopic mass × fraction) is worked
 * term by term and the bar chart follows the same state. Abundances that do not total 100 % keep
 * the last valid result. Render with key={element.id} so a new element starts from reviewed data.
 */
export function AbundanceExplore({ element }: { element: AbundanceElement }) {
  const [percents, setPercents] = useState(() => reviewedPercents(element));
  const [resetCount, setResetCount] = useState(0);
  const input = useMemo(() => ({ element, percents }), [element, percents]);
  const { result, error } = useLastValidResult(input, calculateForInput);
  const load = (next: Record<string, number>) => {
    setPercents(next);
    setResetCount((count) => count + 1);
  };
  const remainder = fillRemainder(element, percents);
  const lastIsotope = element.isotopes[element.isotopes.length - 1];

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div className="min-w-0 space-y-4">
        <ModelInputPanel
          key={resetCount}
          legend={`Your ${element.name.toLowerCase()} sample`}
          fields={abundanceFields(element)}
          values={percents}
          onChange={setPercents}
        />
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={!remainder}
            onClick={() => remainder && load(remainder)}
            className="rounded-full border border-hairline bg-white px-4 py-2 text-xs font-extrabold text-ink disabled:cursor-not-allowed disabled:opacity-40"
          >
            Make {element.symbol}-{lastIsotope.massNumber} the remainder
          </button>
          <button
            type="button"
            onClick={() => load(reviewedPercents(element))}
            className="rounded-full border border-hairline bg-white px-4 py-2 text-xs font-extrabold text-ink"
          >
            Reset to reviewed abundances
          </button>
        </div>
        <ExploreChallenges
          question={ABUNDANCE_QUESTION}
          challenges={ABUNDANCE_CHALLENGES}
          result={result && !error ? { element, result } : null}
        />
        {error ? (
          <p role="alert" className="rounded-2xl bg-wash-sunshine px-3 py-2 text-sm font-bold text-ink">
            {error} The chart and working keep the last valid sample.
          </p>
        ) : null}
        <div className="rounded-[1.5rem] border border-brand-sky/25 bg-white p-4">
          {result ? <AbundanceChart element={result.explored} /> : null}
        </div>
      </div>
      {result ? (
        <WorkedStepsPanel
          title="Relative atomic mass working"
          steps={result.steps}
          stepTestIdPrefix="ar-step"
          result={result.summary}
          resultTestId="ar-result"
          limitNote="Isotopic masses are fixed reviewed values; only the abundances are yours. Real samples vary slightly in abundance."
        />
      ) : null}
    </div>
  );
}
